import { useEffect, useMemo, useState } from "react";
import { getBaskets } from "../Utils/storeService";
import { useCart } from "../context/CartContext";

const MAX_RECS = 4;
export const useCartRecommendations = () => {
  const { cartItems, syncAvailability } = useCart();
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(false);

  const cartKey = useMemo(
    () =>
      cartItems
        .map((item) => `${item.id}:${Number(item.farmId) || 0}`)
        .sort()
        .join(","),
    [cartItems]
  );

  useEffect(() => {
    if (!cartKey) {
      setRecs([]);
      setLoading(false);
      return undefined;
    }

    const cartPairs = cartKey
      .split(",")
      .filter(Boolean)
      .map((entry) => {
        const [id, farmId] = entry.split(":");
        return { id: String(id), farmId: Number(farmId) || 0 };
      });

    const farmIds = [...new Set(cartPairs.map((item) => item.farmId).filter(Boolean))];
    if (farmIds.length === 0) {
      setRecs([]);
      setLoading(false);
      return undefined;
    }

    const cartItemIds = new Set(cartPairs.map((item) => item.id));
    let active = true;

    setLoading(true);

    Promise.all(farmIds.map((farmId) => getBaskets(farmId).catch(() => [])))
      .then((results) => {
        if (!active) return;

        const baskets = results.flat();
        // The drawer and sheet let you raise quantities, and a cart restored
        // from localStorage knows nothing about current stock — this fetch is
        // the only fresh catalogue those surfaces see, so it refreshes the caps.
        if (syncAvailability) {
          syncAvailability(
            Object.fromEntries(
              baskets.map((b) => [b.id, b.availableQuantity ?? null])
            )
          );
        }

        const seenIds = new Set();
        const nextRecs = [];

        for (const basket of baskets) {
          const basketId = String(basket.id);
          if (cartItemIds.has(basketId) || seenIds.has(basketId)) continue;
          // Recommending something the farm cannot build is a dead end.
          if (basket.availableQuantity === 0) continue;

          seenIds.add(basketId);
          nextRecs.push({
            id: basket.id,
            farmId: Number(basket.farmId) || 0,
            name: basket.name,
            price: Number(basket.price || 0),
            image: basket.imageUrl || null,
            availableQuantity: basket.availableQuantity ?? null,
          });

          if (nextRecs.length === MAX_RECS) break;
        }

        setRecs(nextRecs);
      })
      .catch(() => {
        if (active) setRecs([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [cartKey, syncAvailability]);

  return { recs, loading };
};
